import { clamp, smoothstep } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData } from './chunk';

/**
 * Earth that has been moved — the land's divergence from the shape the seed
 * gave it.
 *
 * This is the fifth sparse-history store, and the first one that changes the
 * *shape* of the world rather than its colour, its cover or what stands on
 * it. `env` deviates conditions, `overlay` scars, `wear` packs, `things`
 * promotes, `sere` regrows; all of them leave the ground where it was. This
 * moves it.
 *
 * ## Why lenses and not a raster
 *
 * A height raster would be the obvious store and it is the wrong one. A god
 * who raises a hill has made *one thing*, and the store should say so: a
 * lens is `{x, z, r, amp}`, four numbers, and `heightAt` is their sum. That
 * buys four properties a raster would have to work for:
 *
 *  · **Earth is conserved exactly.** Taking hold of the ground writes a
 *    negative lens where you dug and a positive one of equal volume where you
 *    dropped it. A god does not create matter — law 17, *divine power is
 *    leverage, not the suspension of causality*, applied to the ground.
 *  · **Every LOD agrees for free.** The offset is a pure function of position,
 *    so the same vertex moves by the same amount at every rung, and law 8's
 *    no-resize rule survives without a special case.
 *  · **It is tiny.** A century of a god shifting a hill a month is 1,200
 *    lenses, about 40 KiB — against a raster that would have to store every
 *    touched square metre.
 *  · **It composes.** Two overlapping lenses simply add, so a god can work a
 *    ridge up in passes and the result is smooth rather than terraced.
 *
 * ## The falloff is the same one the rest of the world uses
 *
 * `smoothstep(0.35, 1, d/r)` — identical to `EnvField.stamp`, so a raised
 * mound has the same shoulder as a blessed disc and the eye reads them as the
 * same hand. It also has zero derivative at the rim, which matters here in a
 * way it does not for colour: a lens with a hard edge would put a cliff
 * around every hill and break the terrain normals at the seam.
 *
 * ## What it deliberately does not do yet
 *
 * The **simulation's coarse height is unchanged**. `Region.heightAt` is the
 * immutable baseline every band, fire and habitat reading is built on, and it
 * ships to the chunk workers as typed arrays; making it stateful would undo
 * the property the whole world rests on. So today a god can raise a ridge and
 * the people will still walk their old routes over it. That is a real
 * divergence, it is the same shape as "the far canopy does not read env", and
 * it is written down rather than hidden.
 *
 * Pure: no three, no `Math.random`, no clock. What the land is now is a
 * function of what was moved, and nothing else.
 */

/** One act of moving earth: a smooth dome (`amp > 0`) or hollow (`amp < 0`). */
export interface Lens {
  x: number;
  z: number;
  /** Metres to the rim, where the offset reaches zero. */
  r: number;
  /** Peak offset at the centre, in metres. Signed. */
  amp: number;
}

/** The shared falloff — 1 at the centre, 0 at the rim, flat at both ends. */
export const lensFall = (d: number, r: number): number =>
  r <= 0 ? 0 : 1 - smoothstep(0.35, 1, d / r);

/**
 * Volume of one lens, in cubic metres. Solved once numerically rather than
 * left as a magic constant: for this falloff the dome's volume is
 * `k · π r² · amp` with k ≈ 0.4768 — integrated, not guessed: the first value
 * written here was 0.4098 and `tools/earth.ts` caught it on the first run, a
 * 16% error that would have mispriced every landform in the game. It prints
 * the integral and
 * `test/earth.ts` asserts, so a scoop and the mound it becomes can be checked
 * against each other rather than trusted.
 */
export const LENS_VOLUME_K = 0.47675;
export const lensVolume = (r: number, amp: number): number =>
  LENS_VOLUME_K * Math.PI * r * r * amp;

/** What amplitude a given radius needs to hold this much earth. */
export const ampForVolume = (r: number, volume: number): number =>
  r <= 0 ? 0 : volume / (LENS_VOLUME_K * Math.PI * r * r);

/**
 * Cubic metres of earth a whole god can move — the price of digging, set from
 * `tools/earth.ts` rather than from taste.
 *
 * At 50,000 the ladder reads:
 *
 *     a 15 m × 5 m mound      1,700 m³    0.034 of a god   — a few a day
 *     a 30 m × 10 m hill     13,500 m³    0.270            — twelve days of regen
 *     a 45 m × 15 m knoll    45,500 m³    0.910            — most of a god
 *     a 60 m × 20 m ridge   107,800 m³    2.157            — impossible, for now
 *
 * The impossible end is not a special case; it falls out of the curve exactly
 * as `graspCost` makes a mountain impossible. Touch's THE DIVINE HAND (D §9,
 * *max practical Grasp mass ×4*) is what eventually brings a ridge in reach,
 * so the scale of what a god can move becomes a fact about its history rather
 * than an unlock.
 */
export const EARTH_PER_POWER = 50_000;

/** What moving this much earth costs a god, 0..1 of its whole self. */
export const earthCost = (volume: number): number =>
  Math.abs(volume) / EARTH_PER_POWER;

/* ── a handful, which is the shape the price ladder was already describing ──
 *
 * The four sizes above are not four sizes. Every one of them is exactly a
 * third as deep as it is wide, so every one has the same flank — 37.6° — and
 * the ladder is ONE shape at four scales (`tools/handful.ts` §1). That is
 * worth naming, because it turns three separate decisions into none:
 *
 *  · **How deep is a full scoop?** As deep as the hole stands up: `r/3`. It
 *    is the angle of repose the terrain brief still records as missing from
 *    this island, arriving through the back door.
 *  · **What does a full scoop cost?** `k · r³`, since volume is `r² × depth`
 *    and depth is `r/3`. Price is a cube of the radius and nothing else.
 *  · **How wide is a god's hand?** The inverse of that cube. The widest scoop
 *    a god can take to the bottom is `∛(power / k)` — so the hand SHRINKS as
 *    the god spends itself, and D §9's DIVINE HAND (×4 mass) widens it by
 *    ∛4 = 1.59, which is exactly the factor that brings §20's "impossible"
 *    60 × 20 m ridge into reach. The unlock needed no rule.
 *
 * Kept here rather than in the binding so the quoted price and the charged
 * price cannot drift — `LENS_VOLUME_K` was wrong by 16% once already, and a
 * second copy of this arithmetic is how that comes back.
 */

/** A full scoop is a third as deep as it is wide: the ladder's own shape. */
export const REPOSE = 1 / 3;

/** Cost of a full-depth handful, as a fraction of a whole god — a cube in r. */
export const FULL_HAND_K = (LENS_VOLUME_K * Math.PI * REPOSE) / EARTH_PER_POWER;

/** The widest scoop a god holding `power` could dig to the bottom, in metres. */
export const handRadius = (power: number, hand = 1): number =>
  Math.cbrt(Math.max(0, power * hand) / FULL_HAND_K);

/**
 * How deep a scoop of this radius may be dug, given what the god has left.
 *
 * Two ceilings, and the lower one wins: the ground's own repose angle, and
 * the purse. A god with plenty takes a scoop to `r/3`; a god nearly spent
 * takes the same width and gets a dimple, which is the price rule saying what
 * it always said in the only units the player can see.
 */
export const diggableDepth = (r: number, power: number, hand = 1): number =>
  Math.min(r * REPOSE,
           (Math.max(0, power * hand) * EARTH_PER_POWER) / (LENS_VOLUME_K * Math.PI * r * r));

/**
 * The nearest a pile may be dropped to the pit it came out of.
 *
 * Drop it back in and the two lenses sum to nothing: the god has paid full
 * price for flat ground. `lensFall` is exactly zero at the rim, so one pit
 * radius is where the pile's centre stops being reached at all — measured in
 * `tools/handful.ts` §3, where the pair holds its whole relief from 0.63 r
 * and this is the rounder, stricter line.
 */
export const minThrow = (pitRadius: number): number => pitRadius;

/**
 * The narrowest circle this much earth will lie in without standing up
 * steeper than it was dug.
 *
 * Concentration is free — the price reads the pit alone — so without this a
 * 30 × 10 m scoop tipped into a ten-metre circle stands ninety metres tall.
 * That is not a hill, it is a needle, and the rule that forbids it is the one
 * the scoop itself already obeys: `amp ≤ r/3`.
 *
 * It does not forbid concentration, which is the whole point of moving earth
 * (§20: *a wide shallow scoop dropped somewhere narrow stands taller than the
 * pit was deep*). A scoop dug to a third of its width cannot be narrowed at
 * all, because it is already at repose; a shallow one can, and the shallower
 * it is the more it can.
 */
export const spreadForVolume = (volume: number): number =>
  Math.cbrt(Math.abs(volume) / (LENS_VOLUME_K * Math.PI * REPOSE));

/**
 * The island's median slope on the surface the renderer draws, sampled at the
 * LOD-0 vertex spacing (`tools/relief.ts`, and re-measured by
 * `tools/handful.ts` §6 against the same 4 m step).
 */
export const ISLAND_MEDIAN_SLOPE_DEG = 14.3;

/**
 * How much of a full hold makes something the island would notice.
 *
 * §20: *a landform gentler than the ground it sits on is not a landform, it is
 * a wish.* A lens's steepest flank is `atan(2.308 · depth / r)`, so the test
 * is a ratio of depth to radius and nothing else — which is what lets this be
 * one number instead of a table. Below it the gesture has dug nothing and must
 * charge nothing: fire's `BURN_MARK_CELLS` sitting in the gap between weather
 * and history, applied to a hand.
 */
export const MEANINGFUL_HOLD =
  Math.tan((ISLAND_MEDIAN_SLOPE_DEG * Math.PI) / 180) / 2.308 / REPOSE;

/** A scoop the god could take right now: what the hold and the purse allow. */
export interface Scoop {
  /** Pit radius in metres — the wheel's choice, capped by the hand. */
  r: number;
  /** Pit depth in metres — the hold, capped by repose and by the purse. */
  depth: number;
  volume: number;
  /** Fraction of the whole god this would spend. */
  cost: number;
  /** True when the hold has reached the ground's own repose angle. */
  atRepose: boolean;
}

/**
 * What a hold of this length, at this width, in this state, has dug.
 *
 * Pure, and the single source for both the preview and the act — a preview
 * that computed its own volume would drift from the price the god is charged,
 * which is how a 16% error in `LENS_VOLUME_K` survived once already.
 *
 * `held` is a fraction of a full hold, not seconds: how long a full hold
 * *takes* is an interaction constant and has no business in the world model.
 */
export function scoopFor(
  chosenRadius: number, power: number, held: number, hand = 1,
): Scoop {
  const r = Math.max(0, Math.min(chosenRadius, handRadius(power, hand)));
  // ONE ceiling, and that is a result rather than a simplification. The
  // obvious version clamps by repose AND by `diggableDepth`, and the second
  // clamp can never fire: the hand IS the radius at which a full scoop costs
  // everything the god has, so at the hand the two ceilings are equal and
  // anywhere below it the purse is slack. `test/handful.ts` §2 asserts that
  // identity, which is the honest way to keep a branch nobody can take from
  // sitting in here unexamined.
  const depth = clamp(held, 0, 1) * r * REPOSE;
  const volume = lensVolume(r, depth);
  return {
    r, depth, volume,
    cost: earthCost(volume),
    atRepose: depth >= r * REPOSE - 1e-9,
  };
}

/** Has this hold dug anything the island would notice? */
export const scoopReads = (s: Scoop): boolean =>
  s.r > 0 && s.depth >= s.r * REPOSE * MEANINGFUL_HOLD;

export interface EarthSnapshot {
  lenses: Lens[];
}

export class EarthStore {
  /** Insertion order is history order, and it is the order snapshots keep. */
  private readonly lenses: Lens[] = [];
  /** chunk key → indices touching it, so `at()` never scans the island. */
  private readonly index = new Map<string, number[]>();
  private version_ = 0;

  get size(): number { return this.lenses.length; }
  /** Bumps whenever the land's shape changes — a cache tag, not history. */
  get version(): number { return this.version_; }

  all(): readonly Lens[] { return this.lenses; }

  /**
   * Move earth. Returns the lens so a caller can pair a scoop with its mound,
   * and the chunk keys whose ground changed.
   */
  add(lens: Lens): { lens: Lens; dirty: string[] } {
    const i = this.lenses.length;
    this.lenses.push({ ...lens });
    const dirty = this.chunksOf(lens);
    for (const k of dirty) {
      const list = this.index.get(k);
      if (list) list.push(i); else this.index.set(k, [i]);
    }
    this.version_++;
    return { lens: this.lenses[i], dirty };
  }

  /**
   * Take earth from one place and put it in another, conserving volume. The
   * destination amplitude is solved from the source's volume, so a wide
   * shallow scoop becomes a narrow tall mound if that is where it is dropped.
   */
  move(
    from: { x: number; z: number; r: number; depth: number },
    to: { x: number; z: number; r: number },
  ): { dug: Lens; piled: Lens; dirty: string[] } {
    const volume = lensVolume(from.r, from.depth);
    const dug = this.add({ x: from.x, z: from.z, r: from.r, amp: -from.depth });
    const piled = this.add({
      x: to.x, z: to.z, r: to.r, amp: ampForVolume(to.r, volume),
    });
    return {
      dug: dug.lens, piled: piled.lens,
      dirty: [...new Set([...dug.dirty, ...piled.dirty])],
    };
  }

  /** How far the ground here has been moved from where the seed put it. */
  at(x: number, z: number): number {
    if (this.lenses.length === 0) return 0;
    const list = this.index.get(chunkKey(
      Math.floor(x / CHUNK_SIZE), Math.floor(z / CHUNK_SIZE)));
    if (!list) return 0;
    let h = 0;
    for (const i of list) {
      const l = this.lenses[i];
      h += l.amp * lensFall(Math.hypot(x - l.x, z - l.z), l.r);
    }
    return h;
  }

  /** Every chunk the store currently deforms. */
  chunks(): string[] { return [...this.index.keys()]; }

  /**
   * Does anything moved reach this chunk at all?
   *
   * The index already answers this — it is built from each lens's own chunk
   * footprint — so it is one Map lookup, and it is what `applyEarth`'s "cheap
   * reject" was always supposed to be.
   */
  touches(cx: number, cz: number): boolean {
    return this.index.has(chunkKey(cx, cz));
  }

  /** Total earth displaced, both signs — the god's own ledger of its digging. */
  displaced(): number {
    let v = 0;
    for (const l of this.lenses) v += Math.abs(lensVolume(l.r, l.amp));
    return v;
  }

  private chunksOf(l: Lens): string[] {
    const out: string[] = [];
    const c0x = Math.floor((l.x - l.r) / CHUNK_SIZE), c1x = Math.floor((l.x + l.r) / CHUNK_SIZE);
    const c0z = Math.floor((l.z - l.r) / CHUNK_SIZE), c1z = Math.floor((l.z + l.r) / CHUNK_SIZE);
    for (let cz = c0z; cz <= c1z; cz++) for (let cx = c0x; cx <= c1x; cx++) {
      out.push(chunkKey(cx, cz));
    }
    return out;
  }

  toSnapshot(): EarthSnapshot {
    return { lenses: this.lenses.map((l) => ({ ...l })) };
  }

  restore(s: EarthSnapshot | undefined): void {
    this.lenses.length = 0;
    this.index.clear();
    this.version_++;
    for (const l of s?.lenses ?? []) this.add(l);
  }
}

const chunkKey = (cx: number, cz: number) => `${cx},${cz}`;

/**
 * Move a built chunk's ground, and everything standing on it.
 *
 * Runs **first** in the transform chain, before env, wear, overlay, things
 * and sere — those all read positions and tint what is there, and none of
 * them should have to know the ground moved under them. Heights, the four
 * edge skirts (so neighbouring chunks agree about the derivative at the
 * vertices they share) and every vegetation instance shift by the same
 * function of position.
 *
 * Returns the chunk untouched, byte for byte, when nothing has been moved
 * near it — the same property `applyThings` has, and for the same reason:
 * untouched ground must cost nothing.
 */
export function applyEarth(data: ChunkData, earth: EarthStore): ChunkData {
  if (earth.size === 0) return data;

  const step = CHUNK_SIZE / (data.res - 1);
  const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;

  // Cheap reject: nothing in this chunk's own key means nothing to do here.
  //
  // This line used to describe itself accurately and do something else — it
  // scanned every vertex calling `at()`, which is 4,225 lens evaluations at
  // LOD 0 to decide that there is nothing to evaluate, on the main thread
  // (`chunkManager` applies transforms in `onResult`), for every chunk mounted
  // anywhere once a god has moved so much as one spadeful. Measured at 132 µs
  // per untouched LOD-0 chunk against ~30 ns for the lookup.
  //
  // And it was WRONG, not merely slow. A lens smaller than the vertex step,
  // sitting between vertices, is invisible to the scan — so the function
  // returned early and the *vegetation* never rode up with its ground, while
  // the same lens at a finer LOD lifted it. The same tree stood at two heights
  // depending on which LOD happened to be mounted, which is exactly what law 8
  // forbids: measured at 65 breaks in 1,920 placements before this line
  // changed, 0 after (`tools/earthlod.ts`).
  //
  // The index is strictly more inclusive than the scan: any vertex with a
  // nonzero offset lies inside some lens's bounding box, and that box is what
  // built the index. So this rejects less and costs less.
  if (!earth.touches(data.cx, data.cz)) return data;

  for (let j = 0; j < data.res; j++) for (let i = 0; i < data.res; i++) {
    data.height[j * data.res + i] += earth.at(ox + i * step, oz + j * step);
  }

  // The apron one step past each edge, or two neighbouring chunks compute
  // different derivatives at the very vertices they share and the seam grows
  // a lighting crease. Optional on ChunkData because the transform tests
  // build one by hand, so this is guarded rather than assumed.
  if (data.apron) {
    for (let i = 0; i < data.res; i++) {
      const x = ox + i * step;
      data.apron.n[i] += earth.at(x, oz - step);
      data.apron.s[i] += earth.at(x, oz + data.res * step);
    }
    for (let j = 0; j < data.res; j++) {
      const z = oz + j * step;
      data.apron.w[j] += earth.at(ox - step, z);
      data.apron.e[j] += earth.at(ox + data.res * step, z);
    }
  }

  // and everything rooted in it rides up with it — a tree on a raised hill is
  // the same tree, at the same place, standing on higher ground
  for (const inst of data.vegetation) inst.y += earth.at(inst.x, inst.z);

  return data;
}

/** Slope of the moved ground alone, for callers that need it. Radians. */
export const earthGrade = (earth: EarthStore, x: number, z: number, d = 4): number => {
  const gx = earth.at(x + d, z) - earth.at(x - d, z);
  const gz = earth.at(x, z + d) - earth.at(x, z - d);
  return Math.atan(Math.hypot(gx, gz) / (2 * d));
};

/** Clamp helper kept local so the module imports nothing it does not use. */
export const clampAmp = (v: number, max: number): number => clamp(v, -max, max);
